import React, { useCallback, useMemo, memo } from 'react';
import cx from 'clsx';
import Tooltip, { TooltipProps } from 'antd/es/tooltip';

import { IconRcVoteFlag } from '@/assets/proposals';
import { HelperTooltip } from '@/components';
import { CreatePropsalModal, CreatePropsalModalProps } from '@/views/Proposal';
import { useSwitch, useProfileAddress } from '@/hooks';
import { login, useCurrentUserId } from '@/utils';

import style from './index.module.scss';

type ProposalFlagTypes = {
  className?: string;
  children?: React.ReactChild;
  placement?: TooltipProps['placement'];
  offsetX?: number;
  offsetY?: number;
  tooltip?: string;
} & Pick<
  CreatePropsalModalProps,
  | 'initType'
  | 'initUrls'
  | 'initAddrs'
  | 'initExtra'
  | 'initIssueAddr'
  | 'appendDescription'
  | 'initSubType'
  | 'initRule'
>;

const ValidProposalFlag = memo(
  ({
    className,
    children,
    offsetX = 6,
    offsetY = 2,
    placement = 'left',
    tooltip = 'Report incorrect data',
    ...rest
  }: ProposalFlagTypes) => {
    const { on, turnOff, turnOn } = useSwitch();
    const userId = useCurrentUserId();

    const align = useMemo(
      () => ({
        offset: [offsetX, offsetY] as [number, number],
      }),
      [offsetX, offsetY],
    );

    const handleFlagClick = useCallback(() => {
      if (!userId) {
        login();
        return;
      }
      turnOn();
    }, [turnOn, userId]);

    const overlay = useMemo(
      () =>
        tooltip ? (
          <HelperTooltip title={tooltip}>
            <IconRcVoteFlag className={cx(style.icon, className)} onClick={handleFlagClick} />
          </HelperTooltip>
        ) : (
          <IconRcVoteFlag className={cx(style.icon, className)} onClick={handleFlagClick} />
        ),
      [className, tooltip, handleFlagClick],
    );

    return (
      <>
        <Tooltip
          placement={placement}
          align={align}
          overlay={overlay}
          overlayClassName={style.proposalFlagOverlay}
          mouseEnterDelay={0}>
          {children}
        </Tooltip>
        {on ? <CreatePropsalModal turnOff={turnOff} {...rest} /> : null}
      </>
    );
  },
);

export const ProposalFlag = memo(
  ({
    disabled,
    children,
    ...rest
  }: ProposalFlagTypes & {
    disabled?: boolean;
  }) => {
    const [urlAddress] = useProfileAddress();

    return disabled ? (
      <>{children}</>
    ) : (
      <ValidProposalFlag initIssueAddr={urlAddress} {...rest}>
        {children}
      </ValidProposalFlag>
    );
  },
);
